import type { App, DirectiveBinding } from 'vue'

type Cleanup = () => void
const cleanups = new WeakMap<HTMLElement, Cleanup>()

function setup(el: HTMLElement, binding: DirectiveBinding<string | undefined>) {
  // Allow overriding the inner scroll container; default = naive-ui data table.
  const selector = binding.value ?? '.n-scrollbar-container'
  el.classList.add('table-scroll')

  let scrollEl: HTMLElement | null = null

  const update = () => {
    if (!scrollEl) return
    const { scrollLeft, scrollWidth, clientWidth } = scrollEl
    el.classList.toggle('has-left', scrollLeft > 0)
    el.classList.toggle('has-right', scrollLeft + clientWidth < scrollWidth - 1)
  }

  // The inner scroll container may not exist yet on mount, and its size can
  // change as data loads, so observe the wrapper and (re)bind when it appears.
  const ro = new ResizeObserver(() => {
    const found = el.querySelector<HTMLElement>(selector)
    if (found && found !== scrollEl) {
      scrollEl?.removeEventListener('scroll', update)
      scrollEl = found
      scrollEl.addEventListener('scroll', update, { passive: true })
    }
    update()
  })
  ro.observe(el)
  window.addEventListener('resize', update, { passive: true })

  cleanups.set(el, () => {
    ro.disconnect()
    window.removeEventListener('resize', update)
    scrollEl?.removeEventListener('scroll', update)
  })
}

export function registerScrollShadowDirective(app: App) {
  app.directive('scroll-shadow', {
    mounted(el: HTMLElement, binding) {
      setup(el, binding)
    },
    beforeUnmount(el: HTMLElement) {
      cleanups.get(el)?.()
      cleanups.delete(el)
    },
  })
}
